import React, { useState, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { ethers } from 'ethers';
import { DPOS_CONTRACT_ADDRESS, TARGET_VALIDATOR, RPC_URL } from '../../config/networkConfig';
import './WalletButton.css';

const WalletButton: React.FC = () => {
  const { 
    account,
    balance, 
    networkId,
    connect, 
    disconnect, 
    isConnected,
    switchNetwork,
    switchAccount,
  } = useWallet();
  
  const [showInfo, setShowInfo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isAttemptingConnection, setIsAttemptingConnection] = useState(false);
  
  // Validator state
  const [isValidator, setIsValidator] = useState(false);
  const [commission, setCommission] = useState<number | null>(null);
  const [newCommission, setNewCommission] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close info panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.wallet-header') && showInfo) {
        setShowInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInfo]);
  
  // Check if the current account is the validator
  useEffect(() => {
    const checkIsValidator = async () => {
      console.log("Checking validator status, account:", account);
      console.log("Target validator:", TARGET_VALIDATOR);
      
      if (!account || !isConnected) {
        setIsValidator(false);
        return;
      }

      // Case insensitive comparison
      const isCurrentValidator = account.toLowerCase() === TARGET_VALIDATOR.toLowerCase();
      console.log("Is validator:", isCurrentValidator);
      
      setIsValidator(isCurrentValidator);

      // If validator, fetch current commission
      if (isCurrentValidator) {
        try {
          console.log("Fetching validator commission...");
          const provider = new ethers.JsonRpcProvider(RPC_URL);
          
          const dposContract = new ethers.Contract(
            DPOS_CONTRACT_ADDRESS,
            [
              'function getValidator(address validator) view returns (tuple(uint256 total_stake, uint256 commission_reward, uint16 commission, uint256 last_commission_change, uint256 undelegations_count, address owner, string description, string endpoint))'
            ],
            provider
          );
          
          const validatorInfo = await dposContract.getValidator(TARGET_VALIDATOR);
          console.log("Validator info received:", validatorInfo);
          
          // Commission is stored as basis points (10000 = 100%)
          const commissionPercentage = Number(validatorInfo.commission) / 100;
          console.log("Commission percentage:", commissionPercentage);
          
          setCommission(commissionPercentage);
          setNewCommission(commissionPercentage);
        } catch (err) {
          console.error("Error fetching validator info:", err);
        }
      }
    };

    if (isConnected) {
      checkIsValidator();
    }
  }, [account, isConnected]);
  
  // Update commission function
  const updateCommission = async () => {
    if (!account || !newCommission || newCommission === commission || !isValidator) return;

    setError(null);
    setIsUpdating(true);
    
    try {
      if (!window.ethereum) {
        throw new Error("Ethereum provider not found. Please install MetaMask.");
      }
      
      // Use the specific type from ethers
      const provider = new ethers.BrowserProvider(
        // Cast to appropriate Eip1193Provider type
        window.ethereum as ethers.Eip1193Provider
      );
      
      const signer = await provider.getSigner();
      const dposContract = new ethers.Contract(
        DPOS_CONTRACT_ADDRESS,
        [
          'function setCommission(address validator, uint16 commission) external'
        ],
        signer
      );

      // Convert percentage to basis points (e.g., 5.5% -> 550)
      const commissionBasisPoints = Math.round(newCommission * 100);
      console.log(`Setting commission to ${commissionBasisPoints} basis points`);

      const tx = await dposContract.setCommission(TARGET_VALIDATOR, commissionBasisPoints);
      console.log("Transaction sent:", tx.hash);
      
      await tx.wait();
      console.log("Transaction confirmed");

      setCommission(newCommission);
      setIsEditing(false);
    } catch (err: any) {
      console.error("Error updating commission:", err);
      setError(err.message || "Failed to update commission");
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Format address for display
  const formatAddress = (addr: string | null) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };
  
  // Format balance
  const formatBalance = (bal: string) => {
    if (!bal) return '0';
    // Convert from wei to TARA and show 2 decimal places
    const balanceInEther = parseFloat(bal);
    return balanceInEther.toFixed(2);
  };
  
  // Get network name based on ID
  const getNetworkName = (id: string | number | null) => {
    if (!id) return 'Unknown';
    
    // Map network IDs to human-readable names
    const networks: Record<string, string> = {
      '1': 'Ethereum Mainnet',
      '3': 'Ropsten Testnet',
      '4': 'Rinkeby Testnet',
      '5': 'Goerli Testnet',
      '42': 'Kovan Testnet',
      '56': 'Binance Smart Chain',
      '137': 'Polygon Mainnet',
      '80001': 'Polygon Mumbai',
      '841': 'Taraxa Mainnet', // Taraxa mainnet
      '842': 'Taraxa Testnet'  // Taraxa testnet
    };
    
    return networks[id.toString()] || `Network ${id}`;
  };
  
  // Copy address to clipboard
  const copyToClipboard = () => {
    if (account) {
      navigator.clipboard.writeText(account);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Handle connection with local state management
  const handleConnect = async () => {
    try {
      setIsAttemptingConnection(true);
      await connect();
    } catch (error) {
      console.error("Failed to connect:", error);
    } finally {
      setIsAttemptingConnection(false);
    }
  };
  
  // Handle network switch
  const handleNetworkSwitch = async () => {
    try {
      await switchNetwork();
    } catch (error) {
      console.error("Failed to switch network:", error);
      alert("Failed to switch network. Please try again.");
    }
  };
  
  // Handle account switch
  const handleAccountSwitch = async () => {
    try {
      await switchAccount();
    } catch (error) {
      console.error("Failed to switch account:", error);
      alert("Failed to switch account. Please try again.");
    }
  };
  
  // Get button class based on state
  const getButtonClass = () => {
    if (isAttemptingConnection) return 'wallet-button loading';
    if (isConnected) return 'wallet-button connected';
    return 'wallet-button';
  };
  
  // Get button text based on state
  const getButtonText = () => {
    if (isAttemptingConnection) return 'Connecting...';
    if (isConnected) return formatAddress(account);
    return 'Connect Wallet';
  };
  
  // Check if on correct Taraxa network
  const isOnTaraxaNetwork = () => {
    return networkId === '841' || networkId === '842';
  };

  var balanceFormatted = formatBalance(balance);
  console.log("Current balance: ", balance);
  console.log("Formatted balance:", balanceFormatted);
  console.log("Account is validator:", isValidator);
  
  return (
    <div className="wallet-header">
      <button 
        className={getButtonClass()}
        onClick={() => isConnected ? setShowInfo(!showInfo) : handleConnect()}
        disabled={isAttemptingConnection}
      >
        {isAttemptingConnection ? (
          <span className="loading-spinner"></span>
        ) : (
          <span className="icon">
            {isConnected ? '👛' : '🔌'}
          </span>
        )}
        {getButtonText()}
      </button>
      
      {isConnected && showInfo && (
        <div className="wallet-info">
          <div className="wallet-address">
            <span>Address:</span>
            <button className="copy-button" onClick={copyToClipboard}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="wallet-address-value">{formatAddress(account)}</div>
          
          <div className="wallet-balance">
            <span>Balance:</span>
            <span className="balance-value">{formatBalance(balance)} TARA</span>
          </div>
          
          <div className="wallet-network">
            Network: <span className={`network-name ${!isOnTaraxaNetwork() ? 'wrong-network' : ''}`}>
              {getNetworkName(networkId)}
            </span>
          </div>
          
          {/* Network switching options */}
          {!isOnTaraxaNetwork() && (
            <div className="network-options">
              <button className="network-switch-button" onClick={handleNetworkSwitch}>
                Switch to Taraxa Network
              </button>
            </div>
          )}
          
          {/* Validator Commission Controls */}
          {isValidator && commission !== null && (
            <div className="commission-controls">
              <h4>Validator Commission</h4>
              {isEditing ? (
                <div className="commission-editor">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={newCommission || ''}
                    onChange={(e) => setNewCommission(parseFloat(e.target.value))}
                    disabled={isUpdating}
                    className="commission-input"
                  />
                  <span className="percent">%</span>
                  <div className="commission-actions">
                    <button 
                      onClick={updateCommission} 
                      disabled={isUpdating || newCommission === commission}
                      className="commission-btn save"
                    >
                      {isUpdating ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      onClick={() => {
                        setIsEditing(false);
                        setNewCommission(commission);
                        setError(null);
                      }}
                      disabled={isUpdating}
                      className="commission-btn cancel"
                    >
                      Cancel
                    </button>
                  </div>
                  {error && <div className="commission-error">{error}</div>}
                </div>
              ) : (
                <div className="commission-display">
                  <span>Current: {commission}%</span>
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="commission-btn edit"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Account actions */}
          <div className="wallet-actions">
            <button className="action-button" onClick={handleAccountSwitch}>
              Switch Account
            </button>
            <button className="disconnect-button" onClick={disconnect}>
              Disconnect
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletButton;